/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type MouseEvent, useState } from 'react';

import { defineMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { css, cssMap, jsx } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Anchor } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import {
	Card,
	ElementName,
	SmartLinkPosition,
	SmartLinkSize,
	TitleBlock,
} from '@atlaskit/smart-card';
import { token } from '@atlaskit/tokens';

import { ACTION_SUBJECT_PREFIX, EVENT_SOURCE } from '../../constants/analytics';
import { all1pProductKeys, all3pProductKeys } from '../../constants/product-keys';
import {
	LinkDocContentType,
	type ResultClickedCallback,
	type ResultClickedCallbackParams,
} from '../../constants/types';
import { isAuxClick } from '../../utils/clicks';

const fallbackComponentStyles = css({
	display: 'flex',
	gap: token('space.050'),
	paddingTop: token('space.100'),
	paddingRight: token('space.100'),
	paddingBottom: token('space.100'),
	paddingLeft: token('space.100'),
});

const styles = css({
	position: 'relative',
	paddingLeft: token('space.150'),
});

const cardList = cssMap({
	base: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.250'),
	},
	hidden: {
		opacity: 0,
		position: 'absolute',
		top: 0,
	},
	appended: {
		paddingTop: token('space.050'),
	},
	notAppended: {
		paddingTop: token('space.200'),
	},
});

/**
 * Smart cards lazy load when they are in the viewport. This is a behavior that is
 * not desired in this experience. To circumvent this, we set the position of the
 * card to absolute and hide it until the link is resolved. This allows the card to
 * be rendered in the DOM without lazy loading, while still being hidden from view
 * until the link is resolved.
 */
const card = cssMap({
	hidden: {
		opacity: 0,
		position: 'absolute',
		top: 0,
	},
});

const skeleton = cssMap({
	grid: {
		display: 'grid',
		gap: token('space.100'),
		gridTemplateAreas: `
			"icon title"
			"icon meta"
		`,
		gridTemplateColumns: 'min-content 1fr',
		gridTemplateRows: 'auto auto',
	},
	iconArea: {
		gridArea: 'icon',
	},
	titleArea: {
		gridArea: 'title',
	},
	metaArea: {
		gridArea: 'meta',
	},
});

type LinkedAttachmentsProps = {
	links: string[];
	isLinkedResults?: boolean;
	indexOffset?: number;
	parentPosition?: number;
	onResultClicked?: ResultClickedCallback;
	onResultContextMenu?: ResultClickedCallback;
};

export const LinkAttachments: ({
	links,
	isLinkedResults,
	indexOffset,
	parentPosition,
	onResultClicked,
	onResultContextMenu,
}: LinkedAttachmentsProps) => JSX.Element = ({
	links = [],
	isLinkedResults,
	indexOffset,
	parentPosition,
	onResultClicked,
	onResultContextMenu,
}: LinkedAttachmentsProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [linksLoading, setLinksLoading] = useState(links);
	const [linkProductKeys, setLinkProductKeys] = useState<Map<string, string>>(new Map());

	const cards = links.map((link, index) => {
		const resolveLink = (productKey?: string) => {
			if (productKey) {
				setLinkProductKeys((prev) => {
					// Only update if the value has changed to prevent infinite re-renders
					if (prev.get(link) !== productKey) {
						const newMap = new Map(prev);
						newMap.set(link, productKey);
						return newMap;
					}
					return prev;
				});
			}
			if (linksLoading.includes(link)) {
				setLinksLoading((prev) => prev.filter((l) => l !== link));
			}
		};

		return (
			<div
				css={linksLoading.length && card.hidden}
				key={link}
				role="button"
				tabIndex={0}
				onContextMenu={(e) => {
					if (isLinkedResults) {
						e.preventDefault();
						const productKey = linkProductKeys.get(link);
						const fanoutProduct = getProductKey(productKey);

						onResultContextMenu?.({
							sublistPosition: index + (indexOffset ?? 0),
							linkDocContentType: LinkDocContentType.extractedLink,
							fanoutProduct,
						} satisfies ResultClickedCallbackParams);
					}
				}}
				onAuxClick={(e: MouseEvent<HTMLDivElement>) => {
					if (isAuxClick(e) && isLinkedResults) {
						const productKey = linkProductKeys.get(link);
						const fanoutProduct = getProductKey(productKey);

						onResultClicked?.({
							sublistPosition: index + (indexOffset ?? 0),
							linkDocContentType: LinkDocContentType.extractedLink,
							fanoutProduct,
							isAuxClick: true,
						} satisfies ResultClickedCallbackParams);
					}
				}}
			>
				<Card
					url={link}
					appearance="inline"
					ui={{
						hideElevation: true,
						hideBackground: true,
						hidePadding: true,
						size: SmartLinkSize.Medium,
					}}
					onResolve={({ extensionKey }) => {
						resolveLink(extensionKey);
					}}
					onError={({ status }) => {
						resolveLink();
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'error',
								actionSubject: `${ACTION_SUBJECT_PREFIX}SearchResultSmartCard`,
								source: EVENT_SOURCE,
								attributes: {
									status,
								},
							},
						}).fire();
					}}
					onClick={() => {
						window.open(link, '_blank', 'noopener,noreferrer');
						const productKey = linkProductKeys.get(link);
						const fanoutProduct = getProductKey(productKey);

						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'clicked',
								actionSubject: `${ACTION_SUBJECT_PREFIX}SearchResultSmartCard`,
								source: EVENT_SOURCE,
								attributes: {
									position: parentPosition,
									sublistPosition: indexOffset !== undefined ? index + indexOffset : undefined,
								},
							},
						}).fire();

						if (isLinkedResults) {
							onResultClicked?.({
								sublistPosition: index + (indexOffset ?? 0),
								linkDocContentType: LinkDocContentType.extractedLink,
								fanoutProduct,
							} satisfies ResultClickedCallbackParams);
						}
					}}
					placeholder=" "
					fallbackComponent={() => (
						<div css={fallbackComponentStyles}>
							<LinkIcon label="" size="small" />
							<Anchor href={link} target="_blank" rel="noopener noreferrer">
								{link}
							</Anchor>
						</div>
					)}
				>
					<TitleBlock
						hideRetry
						hideIconLoadingSkeleton
						maxLines={1}
						metadata={[{ name: ElementName.State }]}
						subtitle={[{ name: ElementName.CreatedBy }, { name: ElementName.ModifiedOn }]}
						metadataPosition={SmartLinkPosition.Top}
						position={SmartLinkPosition.Top}
					/>
				</Card>
			</div>
		);
	});

	return (
		<div css={styles}>
			{linksLoading.length > 0 && (
				<div css={[cardList.base]}>
					{links.map((link) => (
						<div key={link} css={skeleton.grid}>
							<div css={skeleton.iconArea}>
								<Skeleton
									height="16px"
									width="16px"
									borderRadius={token('radius.small')}
									isShimmering
								/>
							</div>
							<div css={skeleton.titleArea}>
								<Skeleton height="16px" width="50%" isShimmering />
							</div>
							<div css={skeleton.metaArea}>
								<Skeleton height="16px" width="85%" isShimmering />
							</div>
						</div>
					))}
				</div>
			)}
			<div
				css={[
					cardList.base,
					linksLoading.length && cardList.hidden,
					isLinkedResults ? cardList.appended : cardList.notAppended,
				]}
			>
				{cards}
			</div>
		</div>
	);
};

export const getLinksFromText: (text: string) => string[] = (text: string): string[] => {
	// URL terminator class (see {@link URL_PATTERN}) excludes whitespace,
	// markdown/HTML closers, and HTML attribute quotes (`"` / `'`) so a URL
	// inside `<img src="https://…/foo.png">` doesn't consume the closing `"`.
	const cleanedText = text.replace(HIGHLIGHT_OPEN_PATTERN, '').replace(HIGHLIGHT_CLOSE_PATTERN, '');

	let matches = cleanedText.match(URL_PATTERN)?.map(String) || [];

	// Check if the text ends with a URL - if so, exclude it as it might be truncated
	if (matches.length > 0) {
		const lastMatch = matches[matches.length - 1];
		const lastMatchIndex = cleanedText.lastIndexOf(lastMatch);
		const textEndsWithLastMatch = lastMatchIndex + lastMatch.length === cleanedText.length;

		// If the text ends with a URL, exclude the last url match
		if (textEndsWithLastMatch) {
			matches = matches.slice(0, -1);
		}
	}

	// Filter out URLs that end with invalid patterns like "...", ".", ",", etc.
	const validMatches = matches.filter((url) => !URL_INVALID_ENDING_PATTERN.test(url));

	// Deduplicate (helpful for markdown like [https://example.com](https://example.com) )
	return [...new Set(validMatches)];
};

/**
 * Returns a normalized form of a URL suitable for *deduplication only*. The
 * original URL string should still be passed to UI components — this output is
 * not safe to render or navigate to.
 *
 * Normalization handles common ways the same resource ends up represented
 * differently across data sources (e.g. UGS-hydrated Jira issue links vs URLs
 * parsed straight out of an excerpt body):
 *
 * - Lower-cases the protocol and host (`HTTPS://Example.COM` → `https://example.com`)
 * - Strips a single trailing `/` from the path so `/foo` and `/foo/` match
 * - Falls back to the raw input if the URL cannot be parsed
 *
 * URL fragments (`#section-1`) are intentionally preserved — different
 * fragments often point at meaningfully different anchors on the same page
 * and should not be collapsed.
 */
export const canonicalizeUrlForDedupe: (url: string) => string = (url: string): string => {
	if (!url) {
		return url;
	}
	try {
		const parsed = new URL(url);
		parsed.protocol = parsed.protocol.toLowerCase();
		parsed.hostname = parsed.hostname.toLowerCase();
		if (parsed.pathname.length > 1 && parsed.pathname.endsWith('/')) {
			parsed.pathname = parsed.pathname.slice(0, -1);
		}
		return parsed.toString();
	} catch {
		return url;
	}
};

/**
 * Merges two or more lists of URLs, preserving the order of the first occurrence
 * and deduplicating using {@link canonicalizeUrlForDedupe} so that variants of
 * the same resource (trailing slash, fragment, casing of host, etc.) collapse
 * into a single entry. The first occurrence wins for the displayed URL.
 */
export const mergeAndDedupeLinks: (...lists: ReadonlyArray<readonly string[]>) => string[] = (
	...lists: ReadonlyArray<readonly string[]>
): string[] => {
	const seen = new Set<string>();
	const result: string[] = [];
	for (const list of lists) {
		for (const link of list) {
			if (!link) {
				continue;
			}
			const key = canonicalizeUrlForDedupe(link);
			if (seen.has(key)) {
				continue;
			}
			seen.add(key);
			result.push(link);
		}
	}
	return result;
};

/**
 * Tags whose entire element (open, children, close) should be dropped when
 * cleaning up an excerpt for plain-text rendering. These are "replaced" /
 * media elements whose meaningful content is the asset they reference, not
 * any inner text — leaving the surrounding text in place would just produce
 * empty shells like `<picture></picture>`.
 *
 * Kept lower-case; the regex below uses the `i` flag.
 */
const REPLACED_HTML_ELEMENTS = [
	'img',
	'picture',
	'source',
	'video',
	'audio',
	'iframe',
	'object',
] as const;

// Pre-compiled regex constants — see `removeHtmlTags` for why each exists.
// Hoisted to module scope so we don't pay V8's regex-compile cost on every
// call. All patterns are linear-time (no nested quantifiers, concrete
// terminators) and safe to share because we never read `.lastIndex` ourselves.
const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
// Paired form (uses a backreference so the open/close tag names must match):
// `<tag …>…</tag>`. Combined into a single alternation across all replaced
// elements so we do one regex pass instead of N.
const REPLACED_PAIRED_PATTERN = new RegExp(
	`<\\s*(${REPLACED_HTML_ELEMENTS.join('|')})\\b[^>]*>[\\s\\S]*?<\\s*/\\s*\\1\\s*>`,
	'gi',
);
// Self-closing / void form: `<tag …/>` or `<tag …>`. Single alternation pass.
const REPLACED_VOID_PATTERN = new RegExp(
	`<\\s*(?:${REPLACED_HTML_ELEMENTS.join('|')})\\b[^>]*?/?>`,
	'gi',
);
// Truncated opening of a replaced element at the tail of the excerpt (no `>`).
const REPLACED_TRUNCATED_TAIL_PATTERN = new RegExp(
	`<\\s*(?:${REPLACED_HTML_ELEMENTS.join('|')})\\b[^>]*$`,
	'i',
);
const GENERIC_TAG_PATTERN = /<\/?[a-zA-Z][^>]*>/g;
const COLLAPSED_SPACES_PATTERN = /[ \t]{2,}/g;
const SPACE_BEFORE_PUNCTUATION_PATTERN = /\s+([.,;!?])/g;

// Pre-compiled patterns shared by `getLinksFromText` and `removeUrlsFromText`.
const URL_PATTERN = /(https?:\/\/[^\s|>)\]"']+)/g;
const URL_INVALID_ENDING_PATTERN = /\.{2,}|[.,;!?]+$/;
const HIGHLIGHT_OPEN_PATTERN = /@@@hl@@@/g;
const HIGHLIGHT_CLOSE_PATTERN = /@@@endhl@@@/g;
const URL_WITH_OPTIONAL_HIGHLIGHTS_PATTERN =
	/(?:@@@hl@@@\s*)?https?:\/\/[^\s|>)\]"']+(?:\s*@@@endhl@@@)?/g;

// Pre-compiled patterns shared by `extractMarkdownAndAnchorLinkHrefs`.
const MARKDOWN_LINK_PATTERN = /(?<!!)\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/g;
const ANCHOR_HREF_PATTERN = /<\s*a\b[^>]*?\bhref\s*=\s*("([^"]+)"|'([^']+)')/gi;
const HTTP_PROTOCOL_PATTERN = /^https?:\/\//i;

/**
 * Pulls http(s) URLs out of "rich" link carriers in a body of text *before*
 * any cleanup passes (`removeMarkdown`, `removeHtmlTags`) consume them. Used
 * to seed the Links expand with URLs that would otherwise be silently dropped
 * when their wrappers are stripped.
 *
 * Currently extracts:
 * - Markdown link `(href)` parts, e.g. `[label](https://example.com)` →
 *   `https://example.com`. Reference-style links (`[label][1]`) and bare
 *   autolinks (`<https://…>`) are intentionally not handled here — bare
 *   autolinks are picked up by {@link getLinksFromText} after cleanup.
 * - `<a href="…">` and `<a href='…'>` attribute values.
 *
 * Image URLs (`<img src>`, `<picture>`, `![alt](url)`) are deliberately *not*
 * extracted: they reference assets, not navigable links, and surfacing them
 * in a "Links" expand creates noise (e.g. screenshot URLs in PR descriptions).
 *
 * Only `http(s)` URLs are returned; relative paths, `mailto:`, etc. are
 * skipped because the Links expand only supports navigable web URLs.
 */
export const extractMarkdownAndAnchorLinkHrefs: (text: string) => string[] = (
	text: string,
): string[] => {
	if (!text) {
		return [];
	}

	const results: string[] = [];

	// Markdown link `[label](url)`. Negative lookbehind in MARKDOWN_LINK_PATTERN
	// excludes the image form `![alt](url)`.
	for (const match of text.matchAll(MARKDOWN_LINK_PATTERN)) {
		results.push(match[1]);
	}

	// `<a href="…">` and `<a href='…'>` (any attribute order).
	for (const match of text.matchAll(ANCHOR_HREF_PATTERN)) {
		const href = match[2] ?? match[3];
		if (href && HTTP_PROTOCOL_PATTERN.test(href)) {
			results.push(href);
		}
	}

	return results;
};

/**
 * Removes HTML tags from a body of text so that excerpts (e.g. GitHub PR
 * descriptions, which support a restricted subset of HTML inside Markdown)
 * render cleanly as plain text instead of showing raw markup.
 *
 * Behaviour:
 * - For "replaced" / media elements ({@link REPLACED_HTML_ELEMENTS}, e.g.
 *   `<img>`, `<picture>`, `<video>`), the entire element (open tag, children,
 *   close tag) is removed. This handles both self-closing forms
 *   (`<img … />`, `<img …>`) and full forms (`<picture><source …/><img …/></picture>`).
 * - HTML comments (`<!-- … -->`) are removed.
 * - All other tags have their open/close markers stripped while the inner
 *   text content is preserved, so `<a href="…">label</a>` becomes `label`
 *   and `<details><summary>title</summary>body</details>` becomes
 *   `titlebody`.
 * - HTML entities are *not* decoded — that's intentional, the existing
 *   excerpt pipeline does not introduce raw entities.
 *
 * This helper is intentionally not a parser — excerpts are short and may
 * contain malformed HTML, so we use a small set of regexes that fail safe
 * (worst case: a stray `<` survives in the rendered output).
 */
export const removeHtmlTags: (text: string) => string = (text: string): string => {
	if (!text) {
		return '';
	}

	let result = text;

	// 1. Drop HTML comments.
	result = result.replace(HTML_COMMENT_PATTERN, '');

	// Note: we intentionally do NOT try to repair *truncated* HTML comments
	// (e.g. an excerpt that starts mid-comment with `…content…-->`). A heuristic
	// like "delete up to the first `-->`" would have unacceptable false-positive
	// risk: arrow operators (`-->`), shell pipes, and other prose constructs
	// would be silently mangled. The cosmetic artifact is preferred.

	// 2. Drop entire replaced/media elements (open tag, children, close tag).
	//    Handles `<img …/>`, `<img …>`, and `<picture>…</picture>`. Paired form
	//    is removed first using a backreference (`\1`) to require matching
	//    open/close tag names so we don't leave a stray closing tag behind
	//    after the void pass.
	result = result.replace(REPLACED_PAIRED_PATTERN, '');
	result = result.replace(REPLACED_VOID_PATTERN, '');

	// 2b. Truncated opening of a replaced/media element at the *tail* of the
	//     excerpt — e.g. `…<img width="307" alt="screenshot" src="https://…`
	//     with no closing `>`. The excerpt windowing on the backend can clip in
	//     the middle of a tag; `removeMarkdown` would otherwise eat just the
	//     leading `<` (its "broken autolink" rule), leaving the attribute soup
	//     visible inline. Restricting this to media element names keeps the
	//     false-positive risk low: regular prose tags like `<details>` or `<a>`
	//     are intentionally NOT included because the text *after* them is
	//     meaningful and dropping the tail would lose content. Only triggers
	//     when there is no `>` anywhere after the `<tag` — i.e. the tag is
	//     unambiguously unterminated within the excerpt.
	result = result.replace(REPLACED_TRUNCATED_TAIL_PATTERN, '');

	// 3. Strip remaining tags but keep their inner text content.
	result = result.replace(GENERIC_TAG_PATTERN, '');

	// 4. Tidy up whitespace introduced by tag removal, but preserve newlines.
	result = result.replace(COLLAPSED_SPACES_PATTERN, ' ');
	result = result.replace(SPACE_BEFORE_PUNCTUATION_PATTERN, '$1');

	return result.trim();
};

/**
 * Removes bare http(s) URLs from a body of text so they can be surfaced separately
 * (e.g. in a Links expand) without appearing twice in a result excerpt.
 *
 * Mirrors the URL pattern used by {@link getLinksFromText} so that the same set of
 * tokens that gets pulled into the Links expand is the set of tokens removed from
 * the body. Highlight markers (`@@@hl@@@…@@@endhl@@@`) wrapping a URL are also
 * stripped along with the URL so we don't leave dangling markers behind.
 *
 * - Preserves surrounding text
 * - Collapses whitespace introduced by URL removal so the body reads cleanly
 * - Does not attempt to undo markdown link syntax — pair with `removeMarkdown`
 *   first if the input may contain `[label](url)` style links
 */
export const removeUrlsFromText: (text: string) => string = (text: string): string => {
	if (!text) {
		return '';
	}

	// URL token (URL_WITH_OPTIONAL_HIGHLIGHTS_PATTERN) is kept in sync with
	// `getLinksFromText` so the same characters terminate the URL — including
	// HTML attribute quotes (`"` / `'`).
	return (
		text
			.replace(URL_WITH_OPTIONAL_HIGHLIGHTS_PATTERN, '')
			// Collapse runs of whitespace left behind by URL removal, but preserve newlines
			.replace(COLLAPSED_SPACES_PATTERN, ' ')
			// Trim spaces that now sit immediately before punctuation
			.replace(SPACE_BEFORE_PUNCTUATION_PATTERN, '$1')
			.trim()
	);
};

const getProductKey = (key?: string): string => {
	if (!key) {
		return 'unknown';
	}

	const foundKey1P = all1pProductKeys.find((productKey) => key.includes(productKey));
	if (foundKey1P) {
		return foundKey1P;
	}

	const foundKey3P = all3pProductKeys.find((productKey) => key.includes(productKey));
	if (foundKey3P) {
		return foundKey3P;
	}

	return 'unknown';
};

export const buttonLabel: {
	id: string;
	defaultMessage: string;
	description: string;
} = defineMessage({
	id: 'link-attachments.button.label',
	defaultMessage: '{count, plural, one {# link} other {# links}}',
	description: 'Use to describe the number of links shared in a conversation',
});
